import { z } from 'zod';
import { numberStringsIntoNumbers } from './parseObjectValues';
import { FormState } from 'react-hook-form';
import isURL from 'validator/lib/isURL';

export const validateCurrency = () =>
  z.preprocess((value) => {
    if (typeof value === 'number') return value;
    return value === '' ? null : (value as string).replace(',', '.');
  }, z.coerce.number({ invalid_type_error: 'Valor inválido.' }).nonnegative('O valor não pode ser negativo.').nullable());

// Usar somente a propriedade dirtyFields, do formState do RHF, para verificar se o formulário
// foi alterado é insuficiente porque os campos numéricos são convertidos de 'number' para 'string'
// pelo comportamento padrão de <inputs /> HTML.
export const isFormDirty = (
  defaultValues: any,
  newValues: any,
  options: IsFormDirtyOptions = { transformNumberStrings: true },
) => {
  const finalNewValues = options.transformNumberStrings
    ? numberStringsIntoNumbers(newValues)
    : newValues;

  return JSON.stringify(defaultValues) !== JSON.stringify(finalNewValues);
};

// A propriedade dirtyFields, do formState do RHF, retorna um objeto no formato:
// { <campoAlterado1>: true, <campoAlterado2>: true, ... }
export const stripNonChangedFields = (
  obj: Record<string, any>,
  dirtyFields: FormState<any>['dirtyFields'],
) =>
  Object.fromEntries(
    Object.entries(obj).filter((item) => dirtyFields[item[0]]),
  );

export const cleanDocument = (document: unknown) =>
  String(document ?? '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase();

// CPF é sempre numérico com até 11 dígitos. Qualquer documento que contenha
// letras (novo CNPJ alfanumérico) ou ultrapasse 11 caracteres é um CNPJ.
export const isCpf = (document: any) => {
  const cleaned = cleanDocument(document);
  return cleaned.length <= 11 && !/[A-Z]/.test(cleaned);
};

type IsFormDirtyOptions = {
  transformNumberStrings: boolean;
};

export const validatePercent = () =>
  z
    .preprocess((value) => {
      if (typeof value === 'number') return value;
      return value === '' ? null : (value as string).replace(',', '.');
    }, z.coerce.number({ invalid_type_error: 'Valor inválido.' }).nonnegative('O valor não pode ser negativo.').min(1, 'O valor mínimo é 1%.').max(100, 'O valor máximo é 100%.').multipleOf(0.01, 'Valor inválido.'))
    .transform((val) => Number((val / 100).toFixed(4)));

export const validateUrl = () =>
  z
    .string()
    .trim()
    .refine(
      (s) => isURL(s, { require_protocol: true }),
      'O link deve ser uma URL válida. Ex.: https://vibx.com.br',
    );
